import React, { useEffect, useState } from 'react';
import './App.css';
import heroBg from './images/gaming-hero-bg.png';
import featuresBg from './images/gaming-features.png';
import genresBg from './images/gaming-genres.png';
import entertainmentBg from './images/gaming-entertainment.png';
import mobileImg from './images/gaming-mobile.png';
import logoImg from './images/logo.png';

function App() {

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    const sections = document.querySelectorAll('.observe-section');
    sections.forEach((section) => observer.observe(section));

    return () => {
      sections.forEach((section) => observer.unobserve(section));
    };
  }, []);


  const scrollToFeatures = () => {
    document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' });
  };

  const navigateToPlay = () => {
    window.location.href = '/s';
  };

  const content = {
    hero: {
      title: "Wonderful Sunrise",
      subtitle: "We deliver outstanding gaming experiences through cutting-edge technology, community-focused features, and dedicated support for gamers across the globe.",
      cta: "Start Playing"
    },
    features: {
      title: "Why Choose Wonderful Sunrise?",
      items: [
        {
          title: "Games",
          description: "Enjoy the newest mobile games, ready to play anytime right on our portal.",
          icon: "🎮"
        },
        {
          title: "Genres",
          description: "From action to puzzle, our games span many genres — there's something for every player to discover",
          icon: "🎯"
        },
        {
          title: "Unlimited entertainment",
          description: "Non-stop entertainment awaits! Our mobile games are perfect for players of all ages.",
          icon: "⚡"
        }
      ]
    },
    stats: {
      title: "Gaming Excellence",
      subtitle: "Experience the future of mobile gaming with our cutting-edge platform.",
      items: [
        "1000+ Games Available",
        "50+ Game Genres",
        "24/7 Support",
        "Cross-Platform Play"
      ]
    },
    process: {
      title: "Start Your Gaming Journey",
      subtitle: "Join millions of players worldwide and discover your next favorite game.",
      stepsTitle: "3 steps to gaming paradise",
      steps: [
        "Browse our game library",
        "Choose your favorite genre",
        "Start playing instantly"
      ]
    },
    faq: {
      title: "Frequently Asked Questions",
      items: [
        {
          question: "How do I start playing?",
          answer: "Simply browse our game library, select a game that interests you, and click play. No downloads required - everything runs in your browser!"
        },

        {
          question: "Do I need to create an account?",
          answer: "No account required! You can start playing immediately. However, creating an account allows you to save your progress and compete on leaderboards."
        }
      ]
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
          <div className="absolute inset-0 bg-cover bg-center opacity-20" style={{ backgroundImage: `url(${heroBg})` }}></div>
          <div className="absolute inset-0 bg-gradient-to-br from-purple-600/20 via-blue-600/20 to-cyan-600/20"></div>
        </div>

        {/* Floating Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-20 h-20 bg-purple-500/20 rounded-full animate-pulse"></div>
          <div className="absolute top-40 right-20 w-16 h-16 bg-cyan-500/20 rounded-full animate-bounce"></div>
          <div className="absolute bottom-40 left-20 w-12 h-12 bg-pink-500/20 rounded-full animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-24 h-24 bg-blue-500/20 rounded-full animate-bounce"></div>
        </div>

        <div className="relative z-10 text-center px-6 max-w-6xl mx-auto">
          <div className="mb-8">
            <div className="w-32 h-32 mx-auto mb-8 bg-gradient-to-br from-purple-500 to-cyan-500 rounded-full flex items-center justify-center shadow-2xl animate-pulse overflow-hidden">
              <img
                src={logoImg}
                alt="Wonderful Sunrise Logo"
                className="w-full h-full object-contain"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'block';
                }}
              />
              <span className="text-6xl hidden">🌅</span>
            </div>
            <h1 className="text-6xl md:text-8xl font-bold text-white mb-6 leading-tight bg-gradient-to-r from-purple-400 via-pink-400 to-cyan-400 bg-clip-text text-transparent">
              {content.hero.title}
            </h1>
          </div>

          <p className="text-xl md:text-2xl text-gray-300 mb-12 max-w-4xl mx-auto leading-relaxed">
            {content.hero.subtitle}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={navigateToPlay}
              className="px-8 py-4 bg-gradient-to-r from-purple-500 to-cyan-500 rounded-full text-white font-bold text-lg hover:scale-105 transform transition-all duration-300 shadow-xl hover:shadow-purple-500/50"
            >
              {content.hero.cta}
            </button>
            <button
              onClick={scrollToFeatures}
              className="px-8 py-4 bg-white/10 backdrop-blur-sm rounded-full text-white font-bold text-lg hover:scale-105 transform transition-all duration-300 shadow-lg border border-purple-400/30"
            >
              Explore Games
            </button>
          </div>
        </div>

        <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 text-white animate-bounce">
          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
          </svg>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="observe-section py-20 px-6 bg-gradient-to-br from-slate-800 to-purple-900">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-white text-center mb-16">
            {content.features.title}
          </h2>

          <div className="grid md:grid-cols-3 gap-8">
            {content.features.items.map((feature, index) => {
              const images = [mobileImg, genresBg, entertainmentBg];
              return (
                <div key={index} className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-purple-400/20 hover:shadow-2xl hover:shadow-purple-500/20 transition-all duration-300 hover:-translate-y-2 group gaming-card">
                  <div className="w-full h-48 mb-6 rounded-xl overflow-hidden gaming-image-container">
                    <img
                      src={images[index]}
                      alt={feature.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-purple-400 transition-colors">{feature.title}</h3>
                  <p className="text-gray-300 leading-relaxed">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="observe-section py-20 px-6 bg-gradient-to-r from-purple-800 to-cyan-800 relative overflow-hidden">
        {/* Background Gaming Image */}
        <div className="absolute inset-0 bg-cover bg-center opacity-10" style={{ backgroundImage: `url(${featuresBg})` }}></div>
        <div className="absolute inset-0 bg-gradient-to-r from-purple-800/90 to-cyan-800/90"></div>

        <div className="max-w-6xl mx-auto text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            {content.stats.title}
          </h2>
          <p className="text-xl text-gray-300 mb-12 max-w-3xl mx-auto">
            {content.stats.subtitle}
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {content.stats.items.map((item, index) => (
              <div key={index} className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-purple-400/30 shadow-lg hover:shadow-purple-500/20 transition-all duration-300 hover:-translate-y-2">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-cyan-400 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <p className="text-white font-semibold">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mobile Gaming Showcase */}
      <section className="observe-section py-20 px-6 bg-gradient-to-br from-slate-900 to-purple-900 relative overflow-hidden">
        {/* Background Gaming Image */}
        <div className="absolute inset-0 bg-cover bg-center opacity-20" style={{ backgroundImage: `url(${mobileImg})` }}></div>
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/80 to-purple-900/80"></div>

        <div className="max-w-6xl mx-auto text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Mobile Gaming Excellence
          </h2>
          <p className="text-xl text-gray-300 mb-12 max-w-3xl mx-auto">
            Experience gaming like never before with our optimized mobile platform
          </p>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="text-left">
              <h3 className="text-3xl font-bold text-white mb-6">Play Anywhere, Anytime</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-gradient-to-br from-purple-400 to-cyan-400 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="text-xl font-semibold text-white mb-2">Instant Play</h4>
                    <p className="text-gray-300">No downloads required - start playing immediately in your browser</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-gradient-to-br from-purple-400 to-cyan-400 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="text-xl font-semibold text-white mb-2">Cross-Platform</h4>
                    <p className="text-gray-300">Seamless experience across all your devices</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-gradient-to-br from-purple-400 to-cyan-400 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="text-xl font-semibold text-white mb-2">High Performance</h4>
                    <p className="text-gray-300">Optimized for smooth gameplay on any device</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="w-full h-96 rounded-2xl overflow-hidden border-4 border-purple-400/30 shadow-2xl gaming-image-container">
                <img
                  src={mobileImg}
                  alt="Mobile Gaming Experience"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* Simple Image Sections */}
      <section className="py-20 px-6 bg-slate-900">
        <div className="max-w-6xl mx-auto space-y-16">
          {[
            { image: heroBg, title: 'Gaming Hero', text: 'A bold visual that sets the tone for immersive gameplay and vibrant community.' },
            { image: featuresBg, title: 'Platform Features', text: 'Modern tech, smooth performance, and friendly features that keep you playing.' },
            { image: genresBg, title: 'Game Genres', text: 'Action, puzzle, racing and more – explore a library that fits every mood.' },
            { image: entertainmentBg, title: 'Unlimited Entertainment', text: 'Pick up and play anytime. Fresh content and endless fun at your fingertips.' },
            { image: mobileImg, title: 'Mobile Gaming', text: 'Optimized for phones and tablets so you can game anywhere, anytime.' },
          ].map((block, index) => (
            <div key={index} className="grid md:grid-cols-2 gap-8 items-center">
              <div className="rounded-2xl overflow-hidden">
                <img src={block.image} alt={block.title} className="w-full h-80 object-cover" />
              </div>
              <div>
                <h3 className="text-3xl font-bold text-white mb-3">{block.title}</h3>
                <p className="text-gray-300">{block.text}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Genres Section */}
      <section id="genres" className="observe-section py-20 px-6 bg-gradient-to-r from-cyan-800 to-purple-800 relative overflow-hidden">
        {/* Background Gaming Image */}
        <div className="absolute inset-0 bg-cover bg-center opacity-15" style={{ backgroundImage: `url(${genresBg})` }}></div>
        <div className="absolute inset-0 bg-gradient-to-r from-cyan-800/90 to-purple-800/90"></div>

        <div className="max-w-6xl mx-auto text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Game Genres
          </h2>
          <p className="text-xl text-gray-300 mb-12 max-w-3xl mx-auto">
            From action to puzzle, our games span many genres — there's something for every player to discover
          </p>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {[
              "Action", "Adventure", "Puzzle", "Racing", "Strategy", "Sports",
              "RPG", "Simulation", "Arcade", "Platform", "Fighting", "Shooter"
            ].map((genre, index) => (
              <div key={index} className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-cyan-400/30 hover:bg-white/20 transition-all duration-300 hover:scale-105 hover:-translate-y-1">
                <p className="text-white font-semibold text-sm">{genre}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="observe-section py-20 px-6 bg-gradient-to-br from-slate-800 to-purple-900 relative overflow-hidden">
        {/* Background Gaming Image */}
        <div className="absolute inset-0 bg-cover bg-center opacity-10" style={{ backgroundImage: `url(${entertainmentBg})` }}></div>
        <div className="absolute inset-0 bg-gradient-to-br from-slate-800/90 to-purple-900/90"></div>

        <div className="max-w-6xl mx-auto text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            {content.process.title}
          </h2>
          <p className="text-xl text-gray-300 mb-12 max-w-3xl mx-auto">
            {content.process.subtitle}
          </p>

          <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-12 border border-purple-400/20">
            <h3 className="text-3xl font-bold text-white mb-12">
              {content.process.stepsTitle}
            </h3>

            <div className="grid md:grid-cols-3 gap-8">
              {content.process.steps.map((step, index) => (
                <div key={index} className="relative">
                  <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-purple-400/30 shadow-lg hover:shadow-purple-500/20 transition-all duration-300 hover:-translate-y-2">
                    <div className="w-16 h-16 bg-gradient-to-br from-purple-400 to-cyan-400 rounded-full flex items-center justify-center mx-auto mb-6 text-white font-bold text-2xl">
                      {index + 1}
                    </div>
                    <h4 className="text-xl font-bold text-white mb-4">{step}</h4>
                  </div>
                  {index < content.process.steps.length - 1 && (
                    <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                      <svg className="w-8 h-8 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="observe-section py-20 px-6 bg-gradient-to-br from-purple-900 to-slate-900">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-white text-center mb-16">
            {content.faq.title}
          </h2>

          <div className="space-y-6">
            {content.faq.items.map((item, index) => (
              <div key={index} className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-purple-400/20 shadow-lg hover:shadow-purple-500/20 transition-all duration-300">
                <h3 className="text-xl font-bold text-white mb-4">{item.question}</h3>
                <p className="text-gray-300 leading-relaxed">{item.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="observe-section py-20 px-6 bg-gradient-to-r from-purple-600 to-cyan-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Start Gaming?
          </h2>
          <p className="text-xl text-purple-100 mb-8">
            Join millions of players worldwide and discover your next favorite game at Wonderful Sunrise.
          </p>
          <button
            onClick={navigateToPlay}
            className="px-12 py-4 bg-white text-purple-600 font-bold text-xl rounded-full hover:scale-105 transform transition-all duration-300 shadow-xl hover:shadow-white/20"
          >
            Start Playing Now
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 bg-slate-900">
        <div className="max-w-6xl mx-auto">
          {/* Main Footer Content */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-purple-400 to-cyan-400 rounded-full flex items-center justify-center overflow-hidden">
              <img
                src={logoImg}
                alt="Wonderful Sunrise Logo"
                className="w-full h-full object-contain"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'block';
                }}
              />
              <span className="text-2xl hidden">🌅</span>
            </div>
            <h3 className="text-2xl font-bold text-white mb-4">Wonderful Sunrise</h3>
            <p className="text-gray-400 mb-6">
              Outstanding gaming experiences through cutting-edge technology
            </p>
          </div>

          {/* Company Information */}
          <div className="bg-slate-800/50 rounded-lg p-6 border border-slate-700">
            <h4 className="text-lg font-semibold text-white mb-4 text-center">
              ข้อมูลบริษัท:
            </h4>
            <div className="text-gray-300 text-sm leading-relaxed space-y-2 text-center">
              <p><strong>Leadswtich B.V.</strong></p>
              <p>Nassaukade 157HS</p>
              <p>1053LK Amsterdam</p>
              <p>Netherlands</p>
              <p className="mt-4"><strong>This domain and all advertising rights are a property of Leadswtich B.V.</strong></p>
            </div>
          </div>

          {/* Footer Links */}
          <div className="border-t border-slate-700 pt-6 mt-8">
            <div className="flex flex-wrap justify-center gap-6 mb-6">
              <a
                href="/privacy.html"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-white transition-colors duration-300 text-sm"
              >
                Privacy Policy
              </a>
              <a
                href="/terms.html"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-white transition-colors duration-300 text-sm"
              >
                Terms & Conditions
              </a>
              <a
                href="mailto:support@wonderfulsunrise.com"
                className="text-gray-400 hover:text-white transition-colors duration-300 text-sm"
              >
                Support
              </a>
              <a
                href="mailto:contact@wonderfulsunrise.com"
                className="text-gray-400 hover:text-white transition-colors duration-300 text-sm"
              >
                Contact
              </a>
            </div>
          </div>

          {/* Copyright */}
          <div className="text-center">
            <p className="text-gray-500 text-sm">
              © 2024 wonderfulsunrise.com. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;